import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Truck,
  Package,
  CheckCircle,
  Search,
  Minus,
  Plus,
  Briefcase,
  X,
  Calendar,
  Printer,
  ArrowLeft,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fetchEntregasClientes, confirmarEntrega } from '@/services/entregasClientes.service';

import { getClientGarments, getClientAssignedGarments } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

const EntregasEnCamino = ({ idLavanderia, onCountChange, onRefresh, onNavigateToCompletadas }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const printer = getPrinterInstance();
  const [entregas, setEntregas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [familiaMap, setFamiliaMap] = useState(new Map());

  // Modal confirmar entrega
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [entregaToConfirm, setEntregaToConfirm] = useState(null);
  const [confirmItems, setConfirmItems] = useState([]);
  const [recibeNombre, setRecibeNombre] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isLoadingGarments, setIsLoadingGarments] = useState(false);
  const [showAddScreen, setShowAddScreen] = useState(false);



  // Flujo de borrador
  const [draftPrinted, setDraftPrinted] = useState(false);
  const [isPrintingDraft, setIsPrintingDraft] = useState(false);
  const [showReimprimir, setShowReimprimir] = useState(false);

  useEffect(() => {
    loadEntregas();
  }, [idLavanderia]);

  // Cargar mapa de familias una vez para enriquecer items al imprimir
  useEffect(() => {
    getClientGarments({ limit: 500 })
      .then((res) => {
        const map = new Map();
        (res.data || []).forEach((g) => map.set(String(g._id), g.familia || null));
        setFamiliaMap(map);
      })
      .catch(() => {});
  }, []);

  const loadEntregas = async () => {
    setIsLoading(true);
    try {
      const query = {
        'lavanderia.id': idLavanderia,
        tipo: 'Entrega',
        estado: 'En Camino',
      };

      // Si el usuario es conductor, filtrar solo las suyas
      const isConductor = (user?.role ?? user?.rol)?.toLowerCase() === 'conductor';
      if (isConductor) {
        query['conductor.id'] = user?.id ?? user?._id;
      }

      const response = await fetchEntregasClientes({
        query,
        limit: 100,
        page: 1,
      });
      setEntregas(response.data || []);
      onCountChange?.(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar entregas en camino:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas en camino',
        variant: 'destructive',
      });
      setEntregas([]);
      onCountChange?.(0);
    } finally {
      setIsLoading(false);
    }
  };

  const openConfirmModal = async (entrega) => {
    const baseItems = (entrega.items || []).map((item) => ({
      ...item,
      cantidad_confirmada: item.cantidad_alistada,
    }));
    setEntregaToConfirm(entrega);
    setConfirmItems(baseItems);
    setRecibeNombre('');
    setObservaciones('');
    setShowAddScreen(false);
    setDraftPrinted(false);
    setShowReimprimir(false);
    setIsConfirmOpen(true);

    setIsLoadingGarments(true);
    try {
      const clienteId = entrega.cliente?.id || entrega.cliente?._id;
      const res = await getClientAssignedGarments(clienteId);
      const garments = Array.isArray(res.data) ? res.data : [];
      const existingIds = new Set(baseItems.map((i) => String(i.prendaId)));
      const extras = garments
        .filter((g) => {
          const pid = g.prendaId || g.tipoPrenda?._id || g.tipoPrenda?.id || g._id;
          return pid && !existingIds.has(String(pid));
        })
        .map((g) => ({
          prendaId: g.prendaId || g.tipoPrenda?._id || g.tipoPrenda?.id || g._id,
          nombre: g.tipoPrenda?.nombre || g.nombre || 'Prenda',
          peso: g.peso || 0,
          cantidad_alistada: 0,
          cantidad_confirmada: 0,
          _esNueva: true,
        }));
      setConfirmItems((prev) => [...prev, ...extras]);
    } catch {
      // garments load is best-effort
    } finally {
      setIsLoadingGarments(false);
    }
  };

  const updateItemCantidad = (prendaId, value) => {
    setConfirmItems((prev) =>
      prev.map((item) =>
        String(item.prendaId) === String(prendaId)
          ? { ...item, cantidad_confirmada: Math.max(0, value) }
          : item
      )
    );
  };

  // Agrega un item nuevo a la lista principal (solo el botón + lo hace)
  const addItemToMain = (prendaId) => {
    setConfirmItems((prev) =>
      prev.map((item) =>
        String(item.prendaId) === String(prendaId)
          ? { ...item, _inMainList: true, cantidad_confirmada: Math.max(1, item.cantidad_confirmada) }
          : item
      )
    );
  };

  // Quita un item nuevo de la lista principal (solo el botón X lo hace)
  const removeItemFromMain = (prendaId) => {
    setConfirmItems((prev) =>
      prev.map((item) =>
        String(item.prendaId) === String(prendaId)
          ? { ...item, _inMainList: false, cantidad_confirmada: 0 }
          : item
      )
    );
  };

  // Enriquece los items actuales con la familia para agrupar al imprimir
  const buildEnrichedItems = () =>
    confirmItems.map((item) => ({
      ...item,
      familia: familiaMap.get(String(item.prendaId)) || null,
    }));

  // Imprime el recibo (borrador o final) sin llamar al API
  const doPrintRecibo = async (enrichedItems, recipientName) => {
    if (!printer.isBluetoothAvailable()) return;
    if (!printer.isConnected()) {
      toast({ title: 'Conectando impresora...', description: 'Selecciona tu impresora Bluetooth' });
      await printer.connect();
    }
    toast({ title: 'Imprimiendo...', description: 'Generando 2 copias del recibo' });
    await printer.printEntrega({
      ...entregaToConfirm,
      recibe: { nombre: recipientName },
      items: enrichedItems,
      fecha_entrega: new Date(),
    });
  };

  // Imprime borrador sin confirmar en el API
  const handlePrintDraft = async () => {
    setIsPrintingDraft(true);
    try {
      await doPrintRecibo(buildEnrichedItems(), recibeNombre.trim() || 'BORRADOR');
      setDraftPrinted(true);
      toast({ title: 'Borrador impreso', description: 'Verifica las cantidades y confirma cuando estés listo' });
    } catch (printError) {
      const raw = (printError?.message || '').toLowerCase();
      const msg = raw.includes('user cancelled') || raw.includes('user denied')
        ? 'Impresión cancelada'
        : 'No se pudo imprimir el borrador';
      toast({ title: 'Aviso', description: msg, variant: 'destructive' });
    } finally {
      setIsPrintingDraft(false);
    }
  };

  // Confirma la entrega en el API; si reprint=true también imprime el recibo final
  const handleConfirmarEntrega = async ({ reprint = true } = {}) => {
    if (!recibeNombre.trim()) {
      toast({ title: 'Advertencia', description: 'Ingresa el nombre de quien recibe', variant: 'destructive' });
      return;
    }

    setIsConfirming(true);
    setShowReimprimir(false);
    try {
      const result = await confirmarEntrega(
        entregaToConfirm._id,
        confirmItems.map((item) => ({
          prendaId: item.prendaId,
          cantidad_confirmada: item.cantidad_confirmada,
          ...(item._esNueva ? { nombre: item.nombre, peso: item.peso, cantidad_alistada: 0 } : {}),
        })),
        { nombre: recibeNombre.trim() },
        observaciones
      );

      const msg = result?.message || '';
      const hasWarning = msg.toLowerCase().includes('advertencia');

      toast({
        title: hasWarning ? 'Advertencia' : 'Éxito',
        description: hasWarning ? msg : `Entrega ${entregaToConfirm.consecutivo} confirmada`,
        variant: hasWarning ? 'destructive' : 'default',
      });

      if (reprint) {
        try {
          await doPrintRecibo(buildEnrichedItems(), recibeNombre.trim());
        } catch (printError) {
          const raw = (printError?.message || '').toLowerCase();
          const printMsg = raw.includes('user cancelled') || raw.includes('user denied')
            ? 'Impresión cancelada'
            : 'No se pudo imprimir. Puedes reimprimir desde Entregas.';
          toast({ title: 'Aviso', description: printMsg, variant: 'destructive' });
        }
      }

      setIsConfirmOpen(false);
      onNavigateToCompletadas?.();
      onRefresh?.();
    } catch (error) {
      console.error('Error al confirmar entrega:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo confirmar la entrega',
        variant: 'destructive',
      });
    } finally {
      setIsConfirming(false);
    }
  };



  const filteredEntregas = entregas.filter((e) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      e.cliente?.nombre?.toLowerCase().includes(term) ||
      e.consecutivo?.toLowerCase().includes(term) ||
      e.conductor?.nombre?.toLowerCase().includes(term)
    );
  });

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto" />
        <p className="text-sm text-gray-500 mt-4">Cargando entregas en camino...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Búsqueda */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar por cliente, consecutivo o conductor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm h-10"
              />
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {filteredEntregas.length} en camino
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      {filteredEntregas.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Truck className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No hay entregas en camino
            </h3>
            <p className="text-sm text-gray-500">
              Las entregas aparecerán aquí al marcar alistamientos como entregados
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {filteredEntregas.map((entrega) => {
            const totalPrendas = entrega.items?.reduce((s, i) => s + (i.cantidad_alistada || 0), 0) || 0;

            return (
              <motion.div
                key={entrega._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card
                  className="border-l-4 border-l-yellow-400 cursor-pointer active:scale-[0.98] transition-transform"
                  onClick={() => openConfirmModal(entrega)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-semibold text-sm text-yellow-700">
                            {entrega.consecutivo}
                          </span>
                        </div>
                        <p className="font-medium text-sm text-gray-900 truncate">
                          {entrega.cliente?.nombre}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {totalPrendas} prendas
                          </span>
                          {entrega.totalBolsas > 0 && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="h-3 w-3" />
                              {entrega.totalBolsas} bolsa(s)
                            </span>
                          )}
                          {entrega.fecha_envio && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(entrega.fecha_envio).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                          <Truck className="h-3 w-3" />
                          <span>{entrega.conductor?.nombre || 'Sin conductor'}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-green-500 shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}


      {/* Modal Confirmar Entrega - fullscreen mobile */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="!fixed !inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0 !max-w-none !w-full !h-full !rounded-none !p-0 !gap-0 !border-0 flex flex-col [&>button]:hidden">
          <DialogTitle className="sr-only">Confirmar entrega</DialogTitle>
          {entregaToConfirm && (() => {
            const mainItems = confirmItems.filter((i) => !i._esNueva || i._inMainList);
            const availableItems = confirmItems.filter((i) => i._esNueva && !i._inMainList);
            const addedExtras = confirmItems.filter((i) => i._esNueva && i._inMainList).length;
            const totalConf = mainItems.reduce((s, i) => s + (i.cantidad_confirmada || 0), 0);
            const tiposConf = mainItems.filter((i) => i.cantidad_confirmada > 0).length;

            /* ── Pantalla 2: seleccionar prendas ── */
            if (showAddScreen) {
              return (
                <div className="flex flex-col h-full">
                  <div className="bg-blue-600 text-white p-4 flex items-center gap-3 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowAddScreen(false)}
                      className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-blue-700 active:scale-95 transition-all shrink-0"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base font-bold leading-tight">Agregar prendas</h2>
                      <p className="text-blue-100 text-xs truncate">{entregaToConfirm.cliente?.nombre}</p>
                    </div>
                    {addedExtras > 0 && (
                      <span className="bg-white text-green-700 text-xs font-bold px-2.5 py-1 rounded-full shrink-0">
                        {addedExtras} añadida{addedExtras !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {availableItems.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center p-8 gap-3">
                        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="h-7 w-7 text-green-500" />
                        </div>
                        <p className="text-sm font-medium text-gray-700">Todas las prendas ya están incluidas</p>
                        <p className="text-xs text-gray-400">No hay prendas adicionales disponibles</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {availableItems.map((item) => (
                          <button
                            key={String(item.prendaId)}
                            type="button"
                            onClick={() => addItemToMain(item.prendaId)}
                            className="w-full flex items-center gap-4 px-4 py-4 hover:bg-blue-50 active:bg-blue-100 transition-colors text-left border-l-4 border-l-blue-400"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm font-medium text-blue-800 truncate">{item.nombre}</p>
                              </div>
                            </div>
                            <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center shrink-0 shadow-sm">
                              <Plus className="h-4 w-4 text-white" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="p-4 border-t bg-white shrink-0">
                    <Button
                      className="w-full h-11 bg-blue-600 hover:bg-blue-700 gap-2"
                      onClick={() => setShowAddScreen(false)}
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Volver a la entrega
                    </Button>
                  </div>
                </div>
              );
            }

            /* ── Pantalla 1: confirmar entrega ── */
            return (
              <div className="flex flex-col h-full relative overflow-hidden">
                {/* Header */}
                <div className="bg-green-600 text-white p-4 flex items-center justify-between shrink-0">
                  <div>
                    <h2 className="text-lg font-bold flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Confirmar Entrega
                    </h2>
                    <p className="text-green-100 text-sm">
                      {entregaToConfirm.consecutivo} - {entregaToConfirm.cliente?.nombre}
                    </p>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-white hover:bg-green-700 h-8 w-8"
                    onClick={() => setIsConfirmOpen(false)}
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                {/* Content scrollable */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Barra resumen */}
                  <div className="flex items-center gap-4 bg-green-50 rounded-xl p-3 border border-green-100">
                    <div className="text-center">
                      <p className="text-xl font-bold text-green-700">{totalConf}</p>
                      <p className="text-[10px] text-green-600 leading-tight">Prendas</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-gray-500">{tiposConf}</p>
                      <p className="text-[10px] text-gray-500 leading-tight">Tipos</p>
                    </div>
                    <div className="flex-1" />
                    <Label className="text-xs font-semibold text-gray-500">Prendas a entregar</Label>
                  </div>

                  {/* Lista principal */}
                  <div className="space-y-2">
                    {mainItems.map((item) => (
                      <div
                        key={String(item.prendaId)}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                          item._esNueva
                            ? 'bg-blue-50 border-blue-300 border-l-4 border-l-blue-500'
                            : item.cantidad_confirmada > 0
                              ? 'bg-green-50 border-green-200'
                              : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className={`text-sm font-medium truncate ${
                              item._esNueva ? 'text-blue-800' : item.cantidad_confirmada > 0 ? 'text-green-900' : 'text-gray-400'
                            }`}>
                              {item.nombre}
                            </span>
                          </div>
                          {!item._esNueva && (
                            <span className="text-[11px] text-gray-400">Alistadas: {item.cantidad_alistada}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => updateItemCantidad(item.prendaId, item.cantidad_confirmada - 1)}
                            className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100 active:scale-95 transition-all"
                          >
                            <Minus className="h-3.5 w-3.5 text-gray-600" />
                          </button>
                          <input
                            type="number"
                            min="0"
                            value={item.cantidad_confirmada}
                            onChange={(e) => updateItemCantidad(item.prendaId, parseInt(e.target.value) || 0)}
                            className="w-14 text-center text-base font-bold border border-gray-300 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-green-400"
                          />
                          <button
                            type="button"
                            onClick={() => updateItemCantidad(item.prendaId, item.cantidad_confirmada + 1)}
                            className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center hover:bg-blue-600 active:scale-95 transition-all"
                          >
                            <Plus className="h-3.5 w-3.5 text-white" />
                          </button>
                          {item._esNueva && (
                            <button
                              type="button"
                              onClick={() => removeItemFromMain(item.prendaId)}
                              className="w-7 h-7 rounded-full flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-red-50 active:scale-95 transition-all"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Botón agregar prenda → abre pantalla completa */}
                  <button
                    type="button"
                    onClick={() => setShowAddScreen(true)}
                    disabled={isLoadingGarments}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-green-300 text-green-600 text-sm font-medium hover:bg-green-50 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isLoadingGarments ? (
                      <>
                        <div className="h-4 w-4 rounded-full border-2 border-green-400 border-t-transparent animate-spin" />
                        Cargando prendas...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4" />
                        Agregar prenda
                        {availableItems.length > 0 && (
                          <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">
                            {availableItems.length}
                          </span>
                        )}
                      </>
                    )}
                  </button>

                  {/* Quien recibe */}
                  <div className="space-y-2">
                    <Label htmlFor="recibe" className="flex items-center gap-1">
                      Recibe <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="recibe"
                      placeholder="Nombre de quien recibe las prendas"
                      value={recibeNombre}
                      onChange={(e) => setRecibeNombre(e.target.value)}
                      className="h-11"
                    />
                  </div>

                  {/* Observaciones */}
                  <div className="space-y-2">
                    <Label htmlFor="obs">Observaciones (opcional)</Label>
                    <Input
                      id="obs"
                      placeholder="Observaciones..."
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      className="h-11"
                    />
                  </div>
                </div>

                {/* Acciones fijas abajo */}
                <div className="p-4 border-t bg-white shrink-0 space-y-2">
                  {/* Botón borrador */}
                  <Button
                    variant="outline"
                    onClick={handlePrintDraft}
                    disabled={isPrintingDraft || isConfirming}
                    className="w-full h-11 gap-2 border-dashed border-gray-400 text-gray-600 hover:bg-gray-50"
                  >
                    {isPrintingDraft ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500" />
                    ) : (
                      <Printer className="h-4 w-4" />
                    )}
                    {draftPrinted ? 'Reimprimir borrador' : 'Imprimir borrador'}
                    {draftPrinted && !isPrintingDraft && (
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 ml-auto" />
                    )}
                  </Button>

                  {/* Cancelar + Confirmar */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setIsConfirmOpen(false)}
                      disabled={isConfirming}
                      className="flex-1 h-11"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={() =>
                        draftPrinted
                          ? setShowReimprimir(true)
                          : handleConfirmarEntrega({ reprint: true })
                      }
                      disabled={isConfirming || !recibeNombre.trim()}
                      className="flex-1 gap-2 bg-green-600 hover:bg-green-700 h-11"
                    >
                      {isConfirming ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Confirmando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4" />
                          Confirmar
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* ── Overlay: ¿reimprimir antes de confirmar? ── */}
                {showReimprimir && (
                  <div className="absolute inset-0 bg-black/50 z-20 flex items-end">
                    <motion.div
                      initial={{ y: '100%' }}
                      animate={{ y: 0 }}
                      transition={{ type: 'spring', damping: 28, stiffness: 280 }}
                      className="bg-white w-full rounded-t-2xl p-5 space-y-3"
                    >
                      {/* Handle */}
                      <div className="flex justify-center -mt-1 mb-1">
                        <div className="w-10 h-1 rounded-full bg-gray-200" />
                      </div>

                      <div className="text-center space-y-0.5">
                        <h3 className="text-base font-bold text-gray-800">¿Todo correcto?</h3>
                        <p className="text-xs text-gray-500">
                          {entregaToConfirm.consecutivo} · {entregaToConfirm.cliente?.nombre}
                        </p>
                      </div>

                      <Button
                        className="w-full h-12 gap-2 bg-green-600 hover:bg-green-700"
                        onClick={() => handleConfirmarEntrega({ reprint: true })}
                        disabled={isConfirming}
                      >
                        {isConfirming ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        ) : (
                          <Printer className="h-4 w-4" />
                        )}
                        Reimprimir y confirmar
                      </Button>

                      <Button
                        variant="outline"
                        className="w-full h-12 gap-2"
                        onClick={() => handleConfirmarEntrega({ reprint: false })}
                        disabled={isConfirming}
                      >
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        Confirmar sin reimprimir
                      </Button>

                      <Button
                        variant="ghost"
                        className="w-full h-10 text-gray-500"
                        onClick={() => setShowReimprimir(false)}
                        disabled={isConfirming}
                      >
                        Volver a editar
                      </Button>
                    </motion.div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>


    </div>
  );
};


export default EntregasEnCamino;
